import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, X, Loader2, GripVertical, ImageIcon, CheckCircle2 } from "lucide-react";
import { compressImage } from "@/lib/imageCompression";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ImageUploaderProps {
  bucket: string;
  folder?: string;
  value?: string;
  onChange: (url: string) => void;
  className?: string;
}

export const ImageUploader = ({
  bucket,
  folder = "",
  value,
  onChange,
  className = "",
}: ImageUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Image must be less than 10MB", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      // Verify we have a valid session before uploading
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ 
          title: "Not authenticated", 
          description: "Please log in to upload images",
          variant: "destructive" 
        });
        setUploading(false);
        return;
      }

      // Compress image before upload
      const { blob, wasCompressed } = await compressImage(file);
      
      // Use .jpg extension if compressed (converted to JPEG)
      const fileExt = wasCompressed ? "jpg" : file.name.split(".").pop();
      const fileName = `${folder ? folder + "/" : ""}${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, blob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      onChange(publicUrl);
      toast({ 
        title: wasCompressed ? "Image compressed & uploaded!" : "Image uploaded!" 
      });
    } catch (error: any) {
      console.error("Upload error:", error);
      
      // Check if this is an auth-related error
      const isAuthError = error.message?.includes('JWT') || 
                          error.message?.includes('token') ||
                          error.message?.includes('auth') ||
                          error.statusCode === 401 ||
                          error.statusCode === 403;
      
      toast({
        title: "Upload failed",
        description: isAuthError 
          ? "Your session may have expired. Please refresh the page and log in again." 
          : error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  }, [bucket, folder, onChange, toast]);

  const handleRemove = () => {
    onChange("");
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Uploaded"
            className="h-32 w-auto rounded-lg object-cover border border-border"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -top-2 -right-2 h-6 w-6"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <label className="flex h-32 w-full cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/50 hover:bg-muted transition-colors">
          <div className="text-center">
            {uploading ? (
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            ) : (
              <>
                <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">Click to upload</p>
              </>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      )}
    </div>
  );
};

interface SortableImageProps {
  id: string;
  url: string;
  index: number;
  onRemove: (index: number) => void;
}

const SortableImage = ({ id, url, index, onRemove }: SortableImageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isDragging ? "ring-2 ring-primary" : ""}`}
    >
      <img
        src={url}
        alt={`Gallery image ${index + 1}`}
        className="h-24 w-full rounded-lg object-cover border border-border"
      />
      <div
        {...attributes}
        {...listeners}
        className="absolute top-1 left-1 h-6 w-6 flex items-center justify-center bg-background/80 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4 text-foreground" />
      </div>
      <Button
        type="button"
        variant="destructive"
        size="icon"
        className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => onRemove(index)}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

interface UploadProgress {
  fileName: string;
  progress: number;
  status: "pending" | "uploading" | "complete" | "error";
}

interface MultiImageUploaderProps {
  bucket: string;
  folder?: string;
  images: { url: string; id?: string }[];
  onChange: (images: { url: string; id?: string }[]) => void;
}

export const MultiImageUploader = ({
  bucket,
  folder = "",
  images,
  onChange,
}: MultiImageUploaderProps) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((_, i) => `image-${i}` === active.id);
      const newIndex = images.findIndex((_, i) => `image-${i}` === over.id);
      onChange(arrayMove(images, oldIndex, newIndex));
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") && file.size <= 10 * 1024 * 1024
    );

    if (validFiles.length === 0) {
      toast({ title: "No valid images selected", variant: "destructive" });
      return;
    }

    // Initialize progress for all files
    const initialProgress: UploadProgress[] = validFiles.map((file) => ({
      fileName: file.name,
      progress: 0,
      status: "pending" as const,
    }));
    setUploadProgress(initialProgress);
    setUploading(true);

    // Verify we have a valid session before uploading
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({ 
        title: "Not authenticated", 
        description: "Please log in to upload images",
        variant: "destructive" 
      });
      setUploadProgress([]);
      setUploading(false);
      return;
    }

    const newImages: { url: string }[] = [];
    const updatedProgress = [...initialProgress];

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      
      // Update status to uploading (compressing first)
      updatedProgress[i] = { ...updatedProgress[i], status: "uploading", progress: 10 };
      setUploadProgress([...updatedProgress]);

      try {
        // Compress image before upload
        const { blob, wasCompressed } = await compressImage(file);
        
        updatedProgress[i] = { ...updatedProgress[i], progress: 30 };
        setUploadProgress([...updatedProgress]);

        // Use .jpg extension if compressed (converted to JPEG)
        const fileExt = wasCompressed ? "jpg" : file.name.split(".").pop();
        const fileName = `${folder ? folder + "/" : ""}${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        updatedProgress[i] = { ...updatedProgress[i], progress: 50 };
        setUploadProgress([...updatedProgress]);

        const { error } = await supabase.storage.from(bucket).upload(fileName, blob);
        
        updatedProgress[i] = { ...updatedProgress[i], progress: 80 };
        setUploadProgress([...updatedProgress]);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(fileName);
        newImages.push({ url: publicUrl });

        // Mark as complete
        updatedProgress[i] = { ...updatedProgress[i], progress: 100, status: "complete" };
        setUploadProgress([...updatedProgress]);
      } catch (error: any) {
        updatedProgress[i] = { ...updatedProgress[i], status: "error" };
        setUploadProgress([...updatedProgress]);
        
        // Check if this is an auth-related error
        const isAuthError = error.message?.includes('JWT') || 
                            error.message?.includes('token') ||
                            error.message?.includes('auth') ||
                            error.statusCode === 401 ||
                            error.statusCode === 403;
        
        if (isAuthError) {
          toast({
            title: "Session expired",
            description: "Please refresh the page and log in again.",
            variant: "destructive",
          });
          break; // Stop uploading if auth error
        }
        
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    if (newImages.length > 0) {
      onChange([...images, ...newImages]);
      toast({ title: `${newImages.length} image(s) uploaded!` });
    }

    // Clear progress after a delay
    setTimeout(() => {
      setUploadProgress([]);
      setUploading(false);
    }, 1500);
  };

  const removeImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const imageIds = images.map((_, i) => `image-${i}`);
  const totalProgress = uploadProgress.length > 0
    ? Math.round(uploadProgress.reduce((sum, p) => sum + p.progress, 0) / uploadProgress.length)
    : 0;

  return (
    <div className="space-y-4">
      {/* Upload Progress Panel */}
      {uploadProgress.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              Uploading {uploadProgress.filter(p => p.status === "complete").length}/{uploadProgress.length} images
            </span>
            <span className="text-sm text-muted-foreground">{totalProgress}%</span>
          </div>
          <Progress value={totalProgress} className="h-2" />
          <div className="max-h-32 overflow-y-auto space-y-2">
            {uploadProgress.map((file, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                {file.status === "complete" ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                ) : file.status === "error" ? (
                  <X className="h-4 w-4 text-destructive shrink-0" />
                ) : file.status === "uploading" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                ) : (
                  <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className="truncate text-muted-foreground">{file.fileName}</span>
                {file.status === "uploading" && (
                  <span className="ml-auto text-xs text-muted-foreground">{file.progress}%</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={imageIds} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {images.map((img, index) => (
              <SortableImage
                key={`image-${index}`}
                id={`image-${index}`}
                url={img.url}
                index={index}
                onRemove={removeImage}
              />
            ))}

            <label className={`flex h-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/50 hover:bg-muted transition-colors ${uploading ? "pointer-events-none opacity-50" : ""}`}>
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <span className="mt-1 text-xs text-muted-foreground">Add images</span>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
          </div>
        </SortableContext>
      </DndContext>
      {images.length > 1 && !uploading && (
        <p className="text-xs text-muted-foreground">
          Drag images to reorder them
        </p>
      )}
    </div>
  );
};
